import React, { useEffect, useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  Checkbox,
} from "@mui/material";
import { t } from "i18next";
import CancelIcon from "@mui/icons-material/Cancel";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import { primaryColor } from "../../constants/colorConstants";
import { CATEGORY_CODE, TARGET_TYPES } from "../../constants/enums";

const CategoryDetailedViewDialog = (props: any) => {
  const {
    open,
    onClose,
    baseLineValues,
    targetValues,
    categoryList,
    categories,
    targetScope,
    targetType,
    handleTableCellClick,
  } = props;

  const [cellColumns, setCellColumns] = useState<string[]>([]);
  const [currentCategoryIdx, setCurrentCategoryIdx] = useState(0);
  const [showFileUpload, setShowFileUpload] = useState(false);

  const baselineColumns = [
    `Baseline year Scope ${targetScope}`,
    "Emissions(tCO2e)",
    "Included",
    "",
    "Comments",
  ];
  const targetColumns = [
    `Target year Scope ${targetScope}`,
    "Emissions(tCO2e)",
    "%SBT reduction",
    "",
    "Comments",
    "",
  ];

  useEffect(() => {
    setCellColumns(
      targetType === TARGET_TYPES.BASELINE ? baselineColumns : targetColumns
    );
  }, [targetType, targetScope]);

  console.log(baseLineValues, "baseLineValues");

  const handleClose = () => {
    setShowFileUpload(false);
  };

  const shouldDisplayCategory = (code: CATEGORY_CODE) => {
    if (targetScope === 1) {
      return (
        code >= CATEGORY_CODE.CATEGORY_1_1 && code < CATEGORY_CODE.CATEGORY_2_1
      );
    } else if (targetScope === 2) {
      return (
        code >= CATEGORY_CODE.CATEGORY_2_1 && code < CATEGORY_CODE.CATEGORY_3_1
      );
    } else if (targetScope === 3) {
      return code >= CATEGORY_CODE.CATEGORY_3_1;
    }
  };

  const renderBaselineRows = () => {
    let noRelevantEmission = true;

    const rows = categoryList
      .filter((eachCategory: any) => shouldDisplayCategory(eachCategory.code))
      .map((eachCategory: any) => {
        const relevantBaselines = baseLineValues.filter(
          (baseline: { category: number; target_scope: number }) =>
            baseline.target_scope === targetScope
        );

        const relevantEmission = relevantBaselines.reduce(
          (foundEmission: any, baseline: any) => {
            return (
              foundEmission ||
              baseline.emissions.find(
                (emission: any) => emission.category === eachCategory.id
              )
            );
          },
          null
        );
        if (relevantEmission) {
          noRelevantEmission = false;
        }

        return (
          <React.Fragment key={eachCategory.id}>
            {relevantEmission ? (
              <>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="pw-activity-list bg-neutral-50"
                  >
                    {eachCategory.code} {eachCategory.name}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    className={
                      relevantEmission?.is_included ? "" : "bg-neutral-400"
                    }
                  >
                    {relevantEmission?.year}
                  </TableCell>
                  <TableCell
                    className={`pw-cat-emissionVal-${
                      eachCategory.id
                    } pw-no-focus-outline ${
                      relevantEmission?.is_included
                        ? "bg-total-row"
                        : "bg-neutral-400"
                    }`}
                    align="right"
                  >
                    {relevantEmission?.value || 0}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={
                      relevantEmission?.is_included ? "" : "bg-neutral-400"
                    }
                  >
                    <Checkbox
                      checked={relevantEmission?.is_included ? true : false}
                      disabled
                      sx={{
                        color: "#8897AE",
                        "&.Mui-checked": { color: primaryColor },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    className={
                      relevantEmission?.is_included ? "" : "bg-neutral-400"
                    }
                  >
                    <img
                      src="assets/icons/FileUpload.svg"
                      onClick={
                        relevantEmission?.attachments?.files
                          ? () =>
                              handleTableCellClick(
                                relevantEmission?.attachments.files || []
                              )
                          : undefined
                      }
                      alt="Upload"
                    />
                    {relevantEmission?.attachments?.files?.length > 0 && (
                      <sup className="text-xs bg-primary badge">
                        {relevantEmission?.attachments?.files?.length}
                      </sup>
                    )}
                  </TableCell>
                  <TableCell
                    className={
                      relevantEmission?.is_included ? "" : "bg-neutral-400"
                    }
                  >
                    {relevantEmission?.comments}
                  </TableCell>
                </TableRow>
              </>
            ) : null}
          </React.Fragment>
        );
      });

    return (
      <>
        {rows}
        {noRelevantEmission && (
          <TableRow>
            <TableCell colSpan={6} className="pw-activity-list bg-neutral-100">
              Baseline year not defined
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };
  const renderTargetRows = () => {
    const filteredCategories = categories.filter((eachCategory: any) =>
      shouldDisplayCategory(eachCategory.code)
    );

    const handlePrevClick = () => {
      if (currentCategoryIdx > 0) {
        setCurrentCategoryIdx(currentCategoryIdx - 1);
      }
    };

    const handleNextClick = () => {
      if (currentCategoryIdx < filteredCategories.length - 1) {
        setCurrentCategoryIdx(currentCategoryIdx + 1);
      }
    };

    const currentCategory = filteredCategories[currentCategoryIdx];

    const targetYears = targetValues.filter(
      (eachTarget: { target_scope: number; target_coverage: string }) =>
        eachTarget.target_scope === targetScope
    );

    const targetYrs = targetYears.reduce(
      (accumulatedEmissions: any[], target: any) => {
        const matchingEmissions = target.emissions
          .filter((emission: any) => emission.category === currentCategory?.id)
          .map((emission: any) => ({
            ...emission,
            target_sbti_aligned: target.target_sbti_aligned,
            target_alignment: target.target_alignment,
          }));

        return accumulatedEmissions.concat(matchingEmissions);
      },
      []
    );

    console.log(targetYrs, "targetYrs");
    return (
      <>
        {filteredCategories.length > 0 ? (
          <>
            <TableRow key={currentCategory.id}>
              <TableCell colSpan={6} className="pw-activity-list bg-neutral-50">
                {currentCategory.code} {currentCategory.name}
              </TableCell>
            </TableRow>
            {targetYrs?.length
              ? targetYrs.map((eachTarget: any) => (
                  <TableRow>
                    <TableCell>
                      <div className="flex flex-row justify-between">
                        {eachTarget?.year}
                      </div>
                    </TableCell>
                    <TableCell align="right">{eachTarget.value}</TableCell>
                    <TableCell align="right">
                      {eachTarget.reduction_percentage}
                    </TableCell>
                    <TableCell className="cursor-pointer">
                      <img
                        src="assets/icons/FileUpload.svg"
                        onClick={
                          eachTarget?.attachments?.files
                            ? () =>
                                handleTableCellClick(
                                  eachTarget?.attachments.files || []
                                )
                            : undefined
                        }
                        alt="Upload"
                      />
                      {eachTarget?.attachments?.files?.length > 0 ? (
                        <sup className=" text-xs bg-primary badge">
                          {eachTarget?.attachments?.files?.length}
                        </sup>
                      ) : (
                        ""
                      )}
                    </TableCell>
                    <TableCell>{eachTarget?.comments}</TableCell>
                    <TableCell className="cursor-pointer">
                      <div className="flex flex-row justify-evenly text-s">
                        {eachTarget?.target_sbti_aligned ? (
                          <img src="assets/icons/successAlert.svg" />
                        ) : (
                          <img src="assets/icons/infoAlert.svg" />
                        )}{" "}
                        {eachTarget?.target_alignment}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              : ""}
            <TableRow
              key={`${currentCategory.id}-navigation`}
              className="navigation-row"
            >
              <TableCell
                colSpan={6}
                className="pw-activity-list bg-neutral-50 pw-navigation-container"
              >
                <div className="pw-navigation-content">
                  {currentCategoryIdx > 0 && (
                    <div className="flex flex-row gap-2">
                      <div className="text-primary">Previous Category</div>
                      <div
                        onClick={handlePrevClick}
                        className="pw-navigate-next pw-padding-5"
                      >
                        <ArrowLeft />
                      </div>
                    </div>
                  )}
                  {currentCategoryIdx < filteredCategories.length - 1 && (
                    <div className="flex flex-row ml-auto gap-2">
                      <div className="text-primary">Next Category</div>
                      <div
                        onClick={handleNextClick}
                        className="pw-navigate-next pw-padding-5"
                      >
                        <ArrowRight />
                      </div>
                    </div>
                  )}
                </div>
              </TableCell>
            </TableRow>
          </>
        ) : (
          <TableRow>
            <TableCell colSpan={6} className="pw-activity-list bg-neutral-100">
              Target year not defined
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog open={open} className="pw-category-target-dialog">
        <div className="dialog-content">
          <div className="dialog-header">
            <div>
              {targetType === TARGET_TYPES.BASELINE ? "Baseline" : "Target"}{" "}
              Year details
            </div>
            <div onClick={onClose} className="close-icon">
              <CloseIcon />
            </div>
          </div>
          <div className="dialog-body">
            <Card>
              <Table size="small" className="pw-activity-table">
                <TableHead className="pw-activity-table-head">
                  <TableRow>
                    {cellColumns.map((eachCell: any, index: number) => {
                      return (
                        <TableCell
                          key={index}
                          className={`pw-activity-list ${
                            targetType === "BASELINE"
                              ? index === 0
                                ? "w-[446px]"
                                : index === 1
                                ? "w-[212px]"
                                : index === 2
                                ? "w-[50px]"
                                : index === 3
                                ? "w-[50px]"
                                : "w-[250px]"
                              : targetType === "TARGET"
                              ? index === 0
                                ? "w-[375px]"
                                : index === 1
                                ? "w-[126px] bg-total-row"
                                : index === 2
                                ? "w-[157px] bg-total-row"
                                : index === 3
                                ? "w-[50px]"
                                : index === 4
                                ? "w-[203px]"
                                : "w-[158px]"
                              : ""
                          }`}
                          style={{ borderRight: "10px black" }}
                          align={index === 1 ? "center" : "left"}
                        >
                          {t(eachCell)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {targetType === TARGET_TYPES.BASELINE
                    ? renderBaselineRows()
                    : renderTargetRows()}
                </TableBody>
              </Table>
            </Card>
          </div>
        </div>
        <Dialog
          open={showFileUpload}
          onClose={handleClose}
          className="pw-attachment-dialog"
        >
          <div className="pb-6">
            <div className="pw-header-text">Attachments</div>
            <div
              onClick={handleClose}
              style={{
                cursor: "pointer",
                textAlign: "right",
                marginTop: "-30px",
                // padding: "10px",
              }}
            >
              <CancelIcon />
            </div>
          </div>
        </Dialog>
      </Dialog>
    </>
  );
};

export default CategoryDetailedViewDialog;
